import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import {
  getCanonicalCategoryColor,
  toPaletteColorName,
} from '@/features/colors/lib/paletteLookup';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { type ExperienceCategory, useCategoryCache } from './useCategoryCache';
import type {
  UpdateExperienceCategoryMutation,
  UpdateExperienceCategoryMutationVariables,
} from './useUpdateCategory.generated';

const query = gql`
  mutation UpdateExperienceCategory($input: UpdateExperienceCategoryInput!) {
    updateExperienceCategory(input: $input) {
      id
      text
      color
      colorName
      sortOrder
    }
  }
`;

type Context = { prevCategory?: ExperienceCategory };

export const useUpdateCategory = () => {
  const experience = useEffectiveExperience();
  const cache = useCategoryCache();

  const { mutate } = useMutation<
    UpdateExperienceCategoryMutation,
    QueryError,
    UpdateExperienceCategoryMutationVariables,
    Context
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        UpdateExperienceCategoryMutation,
        UpdateExperienceCategoryMutationVariables
      >(query, variables),
    onMutate: ({ input }) => {
      const prevCategory = cache.getCategory(input.categoryId);
      cache.updateCategory({
        id: input.categoryId,
        color: input.color,
        colorName: input.colorName,
        text: input.text,
      });
      return { prevCategory };
    },
    onError: (_error, _variables, context) => {
      if (context?.prevCategory) {
        cache.updateCategory(context.prevCategory);
      }
    },
  });

  const updateCategory = useCallback(
    (
      category: Pick<ExperienceCategory, 'id' | 'text' | 'colorName'>,
      options?: MutateOptions<
        UpdateExperienceCategoryMutation,
        QueryError,
        UpdateExperienceCategoryMutationVariables,
        Context
      >
    ) => {
      // Normalize so the experience never persists a null/unknown slot — a
      // legacy record's first edit converges it to a real colorName plus the
      // matching canonical fallback hex.
      const colorName = toPaletteColorName(category.colorName);
      return mutate(
        {
          input: {
            experienceId: experience.id,
            categoryId: category.id,
            color: getCanonicalCategoryColor(colorName),
            colorName,
            text: category.text || null,
          },
        },
        options
      );
    },
    [mutate, experience.id]
  );

  return { updateCategory };
};
